import { z } from '@hono/zod-openapi'
import { selectUserSchema } from '@server/infrastructure/database/schema/user'
import { langTypeSchema } from '@server/presentation'
import { normalizeLanguageCode } from '@shared/config/i18n/resolve'

export const rawGetUserPodoInfoSchema = z.object({
  id: z.string(),
  name: z.string(),
  phone: z.string().nullable(),
  // 이메일 미제공 LINE 계정의 내부 placeholder는 backend 응답 경계에서 null로 숨긴다.
  email: z.string().nullable(),
  // 계정 이메일을 본인이 변경할 수 있는지(LINE 가입 계정만 true). backend 미배포면 부재할 수 있어 nullish
  email_changeable: z.boolean().nullish(),
  class_type: z.string(),
  lang_type: langTypeSchema,
  access_token: z.string(),
  trial_payment_yn: z.enum(['Y', 'N']),
  trial_class_comp_yn: z.enum(['Y', 'N']),
  payment_yn: z.enum(['Y', 'N']),
  trial_payment_info: z.string().nullable(),
  trial_class_comp_info: z.string().nullable(),
  payment_info: z.string().nullable(),
  remain_ticket_count: z.string(),
  reg_class_count: z.string(),
  is_unlimited_ticket: z.enum(['Y', 'N']),
  company_user_id: z.null(),
  page_call_account: z.null(),
  student_user_token: z.null(),
  apple_refresh_token: z.string().nullable(),
  country_code: z.string().nullish(),
  timezone_code: z.string().nullish(),
  language_code: z.string().nullish(),
  timezoneCode: z.string().nullish(),
  languageCode: z.string().nullish(),
  company_code: z.string().nullish(),
  // 성별 'M'/'F' — GT_USER.GENDER_ENC 복호값(legacy SEX 0/1 폐기). backend 미배포/미설정이면 부재할 수 있어 nullish
  gender: z.string().nullish(),
  // 내 추천코드 (가입 시 생성, 본인이 남에게 공유하는 코드). backend가 노출 전이면 부재할 수 있어 nullish
  recommend_code: z.string().nullish(),
  reg_class_count_for_lang_type_list: z.array(
    z.object({
      lang_type: langTypeSchema,
      reg_class_count: z.number(),
      is_unlimited_ticket: z.enum(['Y', 'N']),
      // backend는 접두사 없는 값을 내려준다 (TRIAL / BASIC / BUSINESS / SMART_TALK).
      // 커리큘럼이 늘어나도 계약이 깨지지 않도록 enum으로 고정하지 않는다.
      curriculum_type: z.string(),
    }),
  ),
})

const rawUserInfoSchema = rawGetUserPodoInfoSchema._def.shape()

export const getUserPodoInfoSchema = z.object({
  id: rawUserInfoSchema.id,
  name: rawUserInfoSchema.name,
  // Public web profile keeps the existing string contract; the BFF maps
  // backend nulls for uncollected LINE PII to an empty form value.
  phone: z.string(),
  email: z.string(),
  // backend 미배포 시 부재 → 기존 동작(변경 불가)을 유지하도록 false 로 정규화한다.
  emailChangeable: rawUserInfoSchema.email_changeable.transform((val) => val ?? false),
  classType: rawUserInfoSchema.class_type,
  langType: rawUserInfoSchema.lang_type,
  accessToken: rawUserInfoSchema.access_token,
  trialPaymentYn: rawUserInfoSchema.trial_payment_yn,
  trialClassCompYn: rawUserInfoSchema.trial_class_comp_yn,
  paymentYn: rawUserInfoSchema.payment_yn,
  trialPaymentInfo: rawUserInfoSchema.trial_payment_info,
  trialClassCompInfo: rawUserInfoSchema.trial_class_comp_info,
  paymentInfo: rawUserInfoSchema.payment_info,
  remainTicketCount: rawUserInfoSchema.remain_ticket_count,
  regClassCount: rawUserInfoSchema.reg_class_count.transform((val) => parseInt(val, 10)),
  isUnlimitedTicket: rawUserInfoSchema.is_unlimited_ticket,
  companyUserId: rawUserInfoSchema.company_user_id,
  pageCallAccount: rawUserInfoSchema.page_call_account,
  studentUserToken: rawUserInfoSchema.student_user_token,
  appleRefreshToken: rawUserInfoSchema.apple_refresh_token,
  countryCode: rawUserInfoSchema.country_code.transform((val) => val ?? 'KR'),
  timezoneCode: rawUserInfoSchema.timezoneCode.transform((val) => val ?? 'Asia/Seoul'),
  languageCode: rawUserInfoSchema.languageCode.transform(normalizeLanguageCode),
  companyCode: rawUserInfoSchema.company_code.transform((val) => val ?? null),
  // 'M'/'F' 외 값(구버전 응답 등)은 미설정(null)으로 정규화
  gender: rawUserInfoSchema.gender.transform((val) => (val === 'M' || val === 'F' ? val : null)),
  recommendCode: rawUserInfoSchema.recommend_code,
  regClassCountForLangTypeList: z.array(
    z.object({
      langType: langTypeSchema,
      regClassCount: z.number(),
      isUnlimitedTicket: z.enum(['Y', 'N']),
      curriculumType: z.string(),
    }),
  ),
})

const userInfoSchema = z
  .object({
    trial_payment_yn: z.string(),
    trial_class_comp_yn: z.string(),
    payment_yn: z.string(),
    trial_payment_info: z.null(),
    trial_class_comp_info: z.string().nullable(),
    payment_info: z.null(),
    remain_ticket_count: z.string(),
    reg_class_count: z.string(),
    is_unlimited_ticket: z.string(),
    company_user_id: z.null(),
    reg_class_count_for_lang_type_list: z.array(z.unknown()),
    // API는 복호화된 논리 필드를 반환하지만 Drizzle 스키마에는 평문 컬럼이 없다.
    phone: z.string().nullable(),
    email: z.string(),
  })
  .merge(
    selectUserSchema.pick({
      id: true,
      name: true,
      classType: true,
      langType: true,
      accessToken: true,
    }),
  )

export const getPodoUserInfoFailureSchema = z.object({
  resultCd: z.literal('404'),
  resultMsg: z.string(),
  registered: z.literal(false),
  userid: z.literal('-1'),
})

export const getPodoUserInfoSuccessSchema = z.object({
  userInfo: userInfoSchema,
  resultCd: z.literal('200'),
  resultMsg: z.string(),
  registered: z.literal(true),
  userid: z.string(),
  USER_TYPE: z.string(),
  USER_TOKEN: z.string(),
})

export type GetRawPodoUserInfo = z.infer<typeof rawGetUserPodoInfoSchema>
export type GetPodoUserInfo = z.infer<typeof getUserPodoInfoSchema>
export type GetPodoUserInfoSuccess = z.infer<typeof getPodoUserInfoSuccessSchema>
export type GetPodoUserInfoFailure = z.infer<typeof getPodoUserInfoFailureSchema>
